import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Form,
  Checkbox,
  Select,
  Table,
  Tabs,
  Button,
  Icon,
  Alert,
  Tooltip,
  message,
  Modal
} from 'antd'
import api from '../../services/api'

const { Option } = Select
const { TabPane } = Tabs

const TranslationSection = ({
  t,
  getFieldDecorator,
  lang,
  langOptions,
  textLanguageCode,
  onTextLanguageChange,
  isGenerateAudio,
  onGenerateAudioChange,
  selectedProvider,
  providerOptions,
  onProviderChange,
  voiceTableData,
  isVoicesLoading,
  selectedVoiceId,
  onVoiceSelect,
  selectedLanguageCode,
  onAudioLanguageChange,
  premiumLanguageOptions,
  ttsLanguageOptions,
  isVoiceFiltersLoading,
  audioLanguageFallbackMessage,
  courseId
}) => {
  const [previewingVoiceId, setPreviewingVoiceId] = useState(null)
  const [previewProgress, setPreviewProgress] = useState(0)
  const [isPreviewGenerating, setIsPreviewGenerating] = useState(false)

  const previewAudioRef = useRef(null)
  const activePreviewTokenRef = useRef(0)
  const previewCacheRef = useRef({})

  const stopPreview = useCallback((skipState = false) => {
    activePreviewTokenRef.current += 1

    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
      previewAudioRef.current.currentTime = 0
      previewAudioRef.current = null
    }

    if (!skipState) {
      setPreviewingVoiceId(null)
      setPreviewProgress(0)
      setIsPreviewGenerating(false)
    }
  }, [])

  useEffect(() => {
    stopPreview()
  }, [selectedProvider, isGenerateAudio, selectedLanguageCode, stopPreview])

  useEffect(() => {
    return () => stopPreview(true)
  }, [stopPreview])

  const getLanguageName = useCallback(
    languageCode => {
      const match = langOptions.find(item => item.code === languageCode)
      if (match && match.name) return match.name.toLowerCase()
      return languageCode || ''
    },
    [langOptions]
  )

  const pollTaskStatus = useCallback(
    async (taskId, token, delay = 1200, onProgress) => {
      while (token === activePreviewTokenRef.current) {
        const statusResponse = await api.aiVideoGeneration.getTaskStatus(taskId)
        const statusData = statusResponse && statusResponse.data
        const statusValue =
          statusData && statusData.status
            ? String(statusData.status).toLowerCase()
            : ''

        if (typeof onProgress === 'function') {
          onProgress(statusData)
        }

        if (
          statusValue === 'success' ||
          statusValue === 'completed' ||
          statusValue === 'done'
        ) {
          return statusData
        }

        if (statusValue === 'failed' || statusValue === 'error') {
          throw new Error('Task failed')
        }

        await new Promise(resolve => setTimeout(resolve, delay))
      }

      throw new Error('Preview cancelled')
    },
    []
  )

  const playAudioUrl = useCallback(
    (previewUrl, voiceId, token) => {
      const audio = new Audio(previewUrl)
      previewAudioRef.current = audio

      audio.addEventListener('timeupdate', () => {
        if (token !== activePreviewTokenRef.current) return
        const duration = audio.duration
        const current = audio.currentTime
        if (isFinite(duration) && duration > 0) {
          setPreviewProgress(Math.min(100, (current / duration) * 100))
        }
      })

      audio.addEventListener('ended', () => {
        if (token !== activePreviewTokenRef.current) return
        setPreviewingVoiceId(null)
        setPreviewProgress(0)
        setIsPreviewGenerating(false)
        previewAudioRef.current = null
      })

      audio.addEventListener('error', () => {
        if (token !== activePreviewTokenRef.current) return
        stopPreview()
        message.warning(t('v4:voice_preview_not_available'))
      })

      setPreviewingVoiceId(voiceId)
      setPreviewProgress(0)
      setIsPreviewGenerating(false)

      audio.play().catch(() => {
        if (token !== activePreviewTokenRef.current) return
        stopPreview()
        message.warning(t('v4:voice_preview_not_available'))
      })
    },
    [stopPreview, t]
  )

  const handlePreviewElevenLabs = useCallback(
    voice => {
      const verifiedLanguages = Array.isArray(voice.verified_languages)
        ? voice.verified_languages
        : []
      const selectedLanguage = String(selectedLanguageCode || '').toLowerCase()
      const languageMatchesSelection = langObj => {
        if (!langObj || !selectedLanguage) return false
        const language =
          typeof langObj === 'string'
            ? langObj
            : langObj.language || langObj.code || langObj.locale
        const locale = typeof langObj === 'string' ? '' : langObj.locale
        const normalizedLanguage = String(language || '').toLowerCase()
        const normalizedLocale = String(locale || '').toLowerCase()

        return (
          normalizedLanguage === selectedLanguage ||
          normalizedLocale === selectedLanguage ||
          normalizedLanguage.indexOf(`${selectedLanguage}-`) === 0 ||
          normalizedLocale.indexOf(`${selectedLanguage}-`) === 0
        )
      }

      const selectedLanguagePreview = verifiedLanguages.find(
        langObj =>
          languageMatchesSelection(langObj) &&
          typeof langObj !== 'string' &&
          langObj.preview_url
      )

      let previewUrl =
        selectedLanguagePreview && selectedLanguagePreview.preview_url
          ? selectedLanguagePreview.preview_url
          : null

      if (!previewUrl) {
        const firstVerifiedPreview = verifiedLanguages.find(
          langObj => typeof langObj !== 'string' && langObj.preview_url
        )
        previewUrl =
          (firstVerifiedPreview && firstVerifiedPreview.preview_url) ||
          voice.preview_url
      }

      if (!previewUrl) {
        message.warning(t('v4:voice_preview_not_available'))
        return
      }

      stopPreview()
      const token = activePreviewTokenRef.current
      playAudioUrl(previewUrl, voice.voice_id, token)
    },
    [playAudioUrl, selectedLanguageCode, stopPreview, t]
  )

  const handlePreviewSharelook = useCallback(
    async voice => {
      const cacheKey = `${selectedLanguageCode || 'en'}::${voice.voice_id}`
      const cachedPreviewUrl = previewCacheRef.current[cacheKey]
      if (cachedPreviewUrl) {
        stopPreview()
        const token = activePreviewTokenRef.current
        playAudioUrl(cachedPreviewUrl, voice.voice_id, token)
        return
      }

      const directPreviewUrl = voice.preview_url || voice.audio_url
      if (directPreviewUrl) {
        stopPreview()
        const token = activePreviewTokenRef.current
        previewCacheRef.current[cacheKey] = directPreviewUrl
        playAudioUrl(directPreviewUrl, voice.voice_id, token)
        return
      }

      stopPreview()
      const token = activePreviewTokenRef.current

      setPreviewingVoiceId(voice.voice_id)
      setPreviewProgress(5)
      setIsPreviewGenerating(true)

      try {
        const synthResponse = await api.aiVideoGeneration.synthesizeVoicePreview(
          {
            video_id: courseId,
            language: getLanguageName(selectedLanguageCode),
            voice_name:
              voice.voice_name ||
              voice.name ||
              voice.voice_id ||
              voice.description ||
              'default_preview_voice'
          }
        )

        if (token !== activePreviewTokenRef.current) return

        const synthData =
          synthResponse && synthResponse.data ? synthResponse.data : {}
        const taskId =
          synthData.task_id ||
          synthData.taskId ||
          synthData.id ||
          (synthData.output && synthData.output.task_id)
        if (!taskId) throw new Error('Missing preview task id')

        const taskData = await pollTaskStatus(
          taskId,
          token,
          1200,
          statusData => {
            if (token !== activePreviewTokenRef.current) return
            const progressValue =
              statusData && typeof statusData.progress === 'number'
                ? statusData.progress
                : null
            if (progressValue !== null) {
              setPreviewProgress(Math.max(5, Math.min(95, progressValue)))
            } else {
              setPreviewProgress(prev => Math.min(95, prev + 5))
            }
          }
        )

        if (token !== activePreviewTokenRef.current) return

        const output = taskData && taskData.output ? taskData.output : {}
        const previewUrl = output.audio_url || output.url || output.output
        if (!previewUrl) throw new Error('Preview url not available')
        previewCacheRef.current[cacheKey] = previewUrl

        setPreviewProgress(98)
        playAudioUrl(previewUrl, voice.voice_id, token)
      } catch (error) {
        if (token !== activePreviewTokenRef.current) return
        stopPreview()
        message.warning(t('v4:voice_preview_not_available'))
      }
    },
    [
      courseId,
      getLanguageName,
      playAudioUrl,
      pollTaskStatus,
      selectedLanguageCode,
      stopPreview,
      t
    ]
  )

  const handlePreviewVoice = useCallback(
    voice => {
      if (
        isPreviewGenerating &&
        previewingVoiceId &&
        previewingVoiceId !== voice.voice_id
      ) {
        return
      }

      if (previewingVoiceId === voice.voice_id) {
        if (isPreviewGenerating) {
          Modal.confirm({
            centered: true,
            title: 'Stop Preview Generation?',
            content:
              'A voice preview is still being generated. Do you want to stop this generation?',
            okText: 'Stop Generation',
            cancelText: 'Continue',
            onOk: () => stopPreview()
          })
        } else {
          stopPreview()
        }
        return
      }

      if (selectedProvider === 'generate-tts-v2') {
        handlePreviewSharelook(voice)
      } else {
        handlePreviewElevenLabs(voice)
      }
    },
    [
      handlePreviewElevenLabs,
      handlePreviewSharelook,
      isPreviewGenerating,
      previewingVoiceId,
      selectedProvider,
      stopPreview
    ]
  )

  const renderPreviewButton = row => {
    const isCurrent = previewingVoiceId === row.voice_id
    const isGenerating = isCurrent && isPreviewGenerating
    const isPlaying = isCurrent && !isPreviewGenerating
    const percent = Math.round(previewProgress)
    const isDisabled =
      isPreviewGenerating &&
      previewingVoiceId &&
      previewingVoiceId !== row.voice_id

    return (
      <Tooltip
        title={isGenerating ? `Generating ${percent}%` : t('v4:preview')}
      >
        <Button
          className={`voice-generate-btn ${isCurrent ? 'active' : ''} ${
            isGenerating ? 'generating' : ''
          }`}
          onClick={e => {
            e.stopPropagation()
            handlePreviewVoice(row)
          }}
          disabled={isDisabled}
        >
          <span className="voice-generate-icon">
            <Icon
              type={
                isGenerating ? 'loading' : isPlaying ? 'pause' : 'caret-right'
              }
            />
          </span>
          <span className="voice-generate-label">
            {isGenerating ? 'Generating' : isPlaying ? 'Playing' : 'Preview'}
          </span>
          <span className="voice-generate-percent">
            {isGenerating ? `${percent}%` : ''}
          </span>
        </Button>
      </Tooltip>
    )
  }

  const visibleProviderOptions =
    Array.isArray(providerOptions) && providerOptions.length
      ? providerOptions
      : []
  const activeProviderValue =
    selectedProvider ||
    (visibleProviderOptions.length ? visibleProviderOptions[0].key : null)
  const activeProviderOption = visibleProviderOptions.find(
    option =>
      option.key === activeProviderValue || option.value === activeProviderValue
  )
  const activeProvider =
    (activeProviderOption && activeProviderOption.key) || activeProviderValue
  const activeProviderService =
    activeProviderOption && activeProviderOption.service
      ? String(activeProviderOption.service).toUpperCase()
      : ''
  const isSharelookProvider =
    activeProvider === 'generate-tts-v2' || activeProviderService === 'TTS'
  const hasAudioProvider = visibleProviderOptions.length > 0
  const currentTextLanguageCode = textLanguageCode || lang
  const currentAudioLanguageCode =
    selectedLanguageCode || currentTextLanguageCode
  const isLanguageOptionsLoading =
    hasAudioProvider &&
    ((isSharelookProvider && isVoiceFiltersLoading) ||
      (!isSharelookProvider && isVoicesLoading && !voiceTableData.length))
  const activeLanguageOptions = !hasAudioProvider
    ? []
    : isSharelookProvider && ttsLanguageOptions && ttsLanguageOptions.length
    ? ttsLanguageOptions
    : premiumLanguageOptions || []
  const isVoiceTableLoading = isVoicesLoading || isLanguageOptionsLoading
  const isTextLanguageDisabled = isGenerateAudio && isVoiceTableLoading

  const normalizedRows = useMemo(() => {
    return voiceTableData
      .map(voice => ({ ...voice }))
      .sort((a, b) => {
        const qualityA =
          typeof a.audio_quality_score === 'number' ? a.audio_quality_score : 0
        const qualityB =
          typeof b.audio_quality_score === 'number' ? b.audio_quality_score : 0
        if (qualityA !== qualityB) return qualityB - qualityA
        return String(a.name || a.voice_id || '').localeCompare(
          String(b.name || b.voice_id || '')
        )
      })
      .map((voice, index) => {
        const hasCleanName =
          voice &&
          voice.name &&
          !String(voice.name)
            .toLowerCase()
            .includes('migrated legacy voice') &&
          !String(voice.name)
            .toLowerCase()
            .includes('voice_id') &&
          !String(voice.name)
            .toLowerCase()
            .includes('voice description')

        const displayName = isSharelookProvider
          ? hasCleanName
            ? voice.name
            : `Default Voice ${index + 1}`
          : hasCleanName
          ? voice.name
          : `Voice ${index + 1}`

        return {
          ...voice,
          displayIndex: index + 1,
          displayName
        }
      })
  }, [isSharelookProvider, voiceTableData])

  const voiceColumns = [
    {
      title: t('v4:voice_name'),
      dataIndex: 'name',
      key: 'name',
      width: '46%',
      render: (_, row) => {
        const useCases = Array.isArray(row.use_case_tags)
          ? row.use_case_tags
          : []
        const tags = useCases.slice(0, 2)
        return (
          <div className="voice-name-cell">
            <div className="voice-name-main" title={row.displayName}>
              {row.displayName}
            </div>
            <div className="voice-meta-line">
              <span>{row.accent || row.language || 'general'}</span>
              <span>{row.pace_label || 'medium pace'}</span>
            </div>
            {!!tags.length && (
              <div className="voice-tag-row">
                {tags.map(tag => (
                  <span
                    key={`${row.voice_id}-${tag}`}
                    className="voice-tag-chip"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: 'Gender',
      key: 'gender',
      width: 100,
      render: (_, row) => (
        <span className="voice-gender-cell">
          {row.gender || (row.labels && row.labels.gender)
            ? String(row.gender || row.labels.gender)
            : 'neutral'}
        </span>
      )
    },
    {
      title: t('v4:preview'),
      key: 'preview',
      width: 170,
      render: (_, row) => renderPreviewButton(row)
    },
    {
      title: t('v4:select_voice'),
      key: 'select',
      width: 140,
      render: (_, row) => {
        const isSelected = selectedVoiceId === row.voice_id
        return (
          <Button
            className={`voice-select-btn ${isSelected ? 'active' : ''}`}
            onClick={e => {
              e.stopPropagation()
              onVoiceSelect(row.voice_id)
            }}
          >
            <span
              className={`voice-select-icon ${isSelected ? 'visible' : ''}`}
            >
              <Icon type="check" />
            </span>
            {isSelected ? t('v4:selected') : t('v4:select')}
          </Button>
        )
      }
    }
  ]

  return (
    <div className="clone-translation-panel">
      <div className="section-head">
        <h3>Translation Settings</h3>
      </div>

      <div className="form-row">
        <label>Text translation language</label>
        <Form.Item colon={false} required={false}>
          {getFieldDecorator('lang', {
            initialValue: currentTextLanguageCode
          })(
            <Select
              key={currentTextLanguageCode}
              showSearch
              optionFilterProp="children"
              defaultValue={currentTextLanguageCode}
              onChange={onTextLanguageChange}
              className="clone-select"
              dropdownClassName="clone-modal-select-dropdown"
              disabled={isTextLanguageDisabled}
            >
              {langOptions.map(item => (
                <Option key={item.code} value={item.code}>
                  {item.name}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </div>

      <div className="form-row audio-toggle-row">
        <Form.Item colon={false} required={false}>
          <Checkbox checked={isGenerateAudio} onChange={onGenerateAudioChange}>
            {t('v5:generate_audio')}
          </Checkbox>
        </Form.Item>
      </div>

      {isGenerateAudio && (
        <div className="clone-voice-panel">
          {!hasAudioProvider ? (
            <div className="audio-service-empty">
              <Icon type="info-circle" />
              <span>
                Your organization does not have an audio service assigned.
                Please contact your administrator.
              </span>
            </div>
          ) : (
            <React.Fragment>
              <div className="form-row">
                <label>Provider</label>
                <Form.Item colon={false} required={false}>
                  <Tabs
                    activeKey={activeProvider}
                    onChange={onProviderChange}
                    className="clone-provider-tabs"
                  >
                    {visibleProviderOptions.map(option => (
                      <TabPane tab={option.label} key={option.key} />
                    ))}
                  </Tabs>
                </Form.Item>
              </div>

              <div className="form-row">
                <label>Audio language</label>
                <Form.Item colon={false} required={false}>
                  <Select
                    showSearch
                    optionFilterProp="children"
                    value={currentAudioLanguageCode}
                    onChange={onAudioLanguageChange}
                    className="clone-select"
                    dropdownClassName="clone-modal-select-dropdown"
                    loading={isLanguageOptionsLoading}
                    disabled={
                      isLanguageOptionsLoading || !activeLanguageOptions.length
                    }
                  >
                    {activeLanguageOptions.map(item => (
                      <Option key={item.code} value={item.code}>
                        {item.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              {audioLanguageFallbackMessage && (
                <Alert
                  className="audio-language-fallback-alert"
                  type="warning"
                  showIcon
                  message={audioLanguageFallbackMessage}
                />
              )}

              <div className="form-row">
                <label>{t('v4:select_voice')}</label>
                <Form.Item colon={false} required={false}>
                  <Table
                    className="clone-voice-table"
                    size="small"
                    columns={voiceColumns}
                    dataSource={normalizedRows}
                    tableLayout="fixed"
                    pagination={false}
                    scroll={{ y: 280 }}
                    loading={isVoiceTableLoading}
                    rowClassName={row =>
                      row.voice_id === selectedVoiceId
                        ? 'voice-row-selected'
                        : ''
                    }
                    onRow={row => ({
                      onClick: () => onVoiceSelect(row.voice_id)
                    })}
                    locale={{
                      emptyText: isVoiceTableLoading
                        ? t('v4:loading_list')
                        : isSharelookProvider
                        ? 'No voices found for the selected language.'
                        : t('v4:loading_list')
                    }}
                  />
                </Form.Item>
              </div>
            </React.Fragment>
          )}
        </div>
      )}
    </div>
  )
}

export default TranslationSection
